import compact from 'lodash/compact';

import useAuth from '@/data/hook/useAuth';
import useAuthorizedUnits from '@/data/hook/shared/useAuthorizedUnits';
import { FIRST_PAGE } from '@/data/hook/shared/usePagination';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import { isAdmin, isTeacher } from '@/functions/auth';
import { getNumberParam } from '@/utils/filters/searchParams';

export type PendencyAdvancedFilters = {
  teacher?: number;
  unit?: number;
};

type PendencyAdvancedParams = PendencyAdvancedFilters & { page: number };

export default function usePendencyAdvancedFilters() {
  const { user } = useAuth();
  const { params, updateParams } =
    useDynamicParams<PendencyAdvancedParams>();

  const userIsTeacher = isTeacher(user?.userType);
  const isAdminUser = isAdmin(user?.userType);
  const { canFilterByUnit, authorizedUnits, isAuthorizedUnit } =
    useAuthorizedUnits();

  const unitParam = getNumberParam(params, 'unit');

  const advancedFilters: PendencyAdvancedFilters = {
    teacher: isAdminUser ? getNumberParam(params, 'teacher') : undefined,
    unit: canFilterByUnit && isAuthorizedUnit(unitParam) ? unitParam : undefined,
  };

  const advancedFiltersCount = compact([
    advancedFilters.teacher,
    advancedFilters.unit,
  ]).length;

  const teacherFilter =
    userIsTeacher && user ? user.id : advancedFilters.teacher;

  const unitsFilter = advancedFilters.unit
    ? [advancedFilters.unit]
    : authorizedUnits;

  const updateAdvancedFilters = (updated: Partial<PendencyAdvancedFilters>) =>
    updateParams({ ...updated, page: FIRST_PAGE });

  const clearAdvancedFilters = () =>
    updateParams({
      teacher: undefined,
      unit: undefined,
      page: FIRST_PAGE,
    });

  return {
    advancedFilters,
    advancedFiltersCount,
    teacherFilter,
    unitsFilter,
    updateAdvancedFilters,
    clearAdvancedFilters,
  };
}
